import { useState, useEffect } from "react";

export type Region = "eg" | "sa" | "other";

interface GeoData {
  region: Region;
  countryCode: string;
}

const REGION_KEY = "detected_region";

function detectFromBrowser(): Region {
  try {
    const lang = navigator.language || (navigator as any).userLanguage || "";
    // Check timezone as a stronger signal
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Riyadh") || tz.includes("Asia/Riyadh")) return "sa";
    if (tz.includes("Cairo") || tz.includes("Africa/Cairo")) return "eg";
    // Fallback to language
    if (lang.startsWith("ar")) return "eg"; // default Arabic to Egypt
    return "other";
  } catch {
    return "eg";
  }
}

export function useRegion() {
  const [region, setRegion] = useState<Region>(() => {
    try {
      const stored = localStorage.getItem(REGION_KEY) as Region;
      if (stored && ["eg", "sa", "other"].includes(stored)) return stored;
    } catch {}
    return detectFromBrowser();
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only fetch IP if no stored preference
    let stored: string | null = null;
    try { stored = localStorage.getItem(REGION_KEY); } catch {}
    if (stored && ["eg", "sa", "other"].includes(stored)) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    // Defer the network request until the browser is idle so it never competes
    // with the LCP image / hero hydration on first load.
    const runFetch = () => fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => r.json())
      .then((data: { country_code?: string; country_name?: string; city?: string }) => {
        const code = (data.country_code || "").toUpperCase();
        let detected: Region = "other";
        if (code === "EG") detected = "eg";
        else if (code === "SA") detected = "sa";
        else if (["AE", "KW", "QA", "BH", "OM"].includes(code)) detected = "sa"; // GCC → SAR pricing
        
        console.log(`🌍 [Region] IP detected: ${data.country_name || code} (${data.city || "?"}) → region: "${detected}" | currency: ${CURRENCY_CONFIG[detected].code} (${CURRENCY_CONFIG[detected].nameAr})`);
        
        setRegion(detected);
        try { localStorage.setItem(REGION_KEY, detected); } catch {}
      })
      .catch((err) => {
        const fallback = detectFromBrowser();
        console.log(`🌍 [Region] IP detection failed (${err.message}), browser fallback → region: "${fallback}" | currency: ${CURRENCY_CONFIG[fallback].code}`);
        setRegion(fallback);
      })
      .finally(() => setLoading(false));

    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(runFetch, { timeout: 3000 });
    } else {
      timeoutHandle = window.setTimeout(runFetch, 1500);
    }

    return () => {
      controller.abort();
      if (idleHandle !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, []);

  const setManualRegion = (r: Region) => {
    setRegion(r);
    try { localStorage.setItem(REGION_KEY, r); } catch {}
  };

  return { region, loading, setRegion: setManualRegion };
}

// Currency formatting helpers
export const CURRENCY_CONFIG = {
  eg: { code: "EGP", symbol: "ج.م", nameAr: "جنيه", nameEn: "EGP" },
  sa: { code: "SAR", symbol: "ر.س", nameAr: "ريال", nameEn: "SAR" },
  other: { code: "EGP", symbol: "ج.م", nameAr: "جنيه", nameEn: "EGP" },
} as const;

// Approximate conversion rates (EGP base)
const EGP_TO_SAR = 0.074; // ~13.5 EGP = 1 SAR

export function convertPrice(egpAmount: number, region: Region): number {
  if (region === "sa") return Math.round(egpAmount * EGP_TO_SAR);
  return egpAmount;
}

export function formatPrice(egpAmount: number, region: Region, lang: "ar" | "en"): string {
  const converted = convertPrice(egpAmount, region);
  const currency = CURRENCY_CONFIG[region];
  if (lang === "ar") {
    return `${converted.toLocaleString("ar-EG")} ${currency.nameAr}`;
  }
  return `${converted.toLocaleString("en")} ${currency.nameEn}`;
}
